import dayjs from 'dayjs';
import { useSnackbar } from 'notistack';
import { useState, useEffect } from 'react';

import { IconMail, IconCalendarStar } from '@tabler/icons-react';

import { AvatarGroupList } from '@/components/Avatar';
import CustomModal from '@/components/CustomModal';
import AdminDetailsSkeleton from '@/components/SkeletonLoader/AdminDetailsSkeleton';
import { Switch } from '@/components/Switch';
import { SectionNameConfig } from '@/constants/constant';
import { ERoleAccess, ECommonStatus, ERestoreEntityType } from '@/enum';
import useAccess from '@/hooks/useAccess';
import { useFormatText } from '@/hooks/useFormatText';
import { updateAdmin, getAdminById } from '@/service/adminServices';
import { blockUnblockRecordAPI } from '@/service/commonService';
import useCommonStore from '@/store/useCommonStore';
import { IAdminDetailsData } from '@/types';
import { getAssetUrl } from '@/utils';

interface IAdminDetailProps {
  setIsOpen: (open: boolean) => void;
  selectedAdminId?: string | number | undefined;
  onRemoveSubmit: () => void;
}

interface ISection {
  id: string;
  section_code: ERoleAccess;
  has_access: boolean;
}

const AdminDetailModal = ({
  setIsOpen,
  selectedAdminId,
  onRemoveSubmit,
}: IAdminDetailProps) => {
  const { hasAccess } = useAccess();

  const { loadingModal, setLoadingModal } = useCommonStore();
  const [adminData, setAdminData] = useState<IAdminDetailsData | null>(null);
  const [sections, setSections] = useState<ISection[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  const { formatText } = useFormatText();
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (selectedAdminId) {
      fetchAdminDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAdminId]);

  const fetchAdminDetails = async () => {
    setLoadingModal(true);
    const response = await getAdminById(selectedAdminId!);
    if (response?.status && response?.data) {
      setAdminData(response?.data);
      setSections(response.data?.sections || []);
    }
    setLoadingModal(false);
  };

  const handlePermissionChange = (id: string) => {
    setSections((prev) =>
      prev.map((section) =>
        section.id === id
          ? { ...section, has_access: !section.has_access }
          : section
      )
    );
  };

  const handleSaveChanges = async () => {
    if (!selectedAdminId) return;
    try {
      setIsSaving(true);
      const payload = {
        sections: sections.map((section) => ({
          id: section.id,
          has_access: section.has_access,
        })),
      };

      const response = await updateAdmin(selectedAdminId.toString(), payload);
      if (response?.status) {
        enqueueSnackbar(response?.message || 'Changes saved successfully', {
          variant: 'success',
        });
        setIsOpen(false);
      } else {
        enqueueSnackbar(response?.message || 'Failed to save changes', {
          variant: 'error',
        });
      }
    } catch (error) {
      console.error('Error saving permissions:', error);
      enqueueSnackbar('An error occurred while saving changes', {
        variant: 'error',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleStatusChange = async () => {
    if (!adminData || !selectedAdminId) return;
    try {
      const is_active =
        adminData.status === ECommonStatus.ACTIVE
          ? ECommonStatus.IN_ACTIVE
          : ECommonStatus.ACTIVE;
      const params = {
        id: selectedAdminId.toString(),
        record_type: ERestoreEntityType.ADMIN,
        is_block: adminData.status === ECommonStatus.ACTIVE,
      };
      const response = await blockUnblockRecordAPI(params);
      if (response?.status) {
        setAdminData((prev) => (prev ? { ...prev, status: is_active } : null));
        onRemoveSubmit();
      }
    } catch (error) {
      console.error('Error updating admin status:', error);
      enqueueSnackbar('Failed to update admin status', { variant: 'error' });
    }
  };

  const activePermissionsCount = sections.filter(
    (section) => section.has_access
  ).length;

  if (!hasAccess(ERoleAccess.USER_MANAGEMENT)) return null;

  return (
    <CustomModal
      setIsOpen={setIsOpen}
      isHeader
      isFooter
      loadingModal={loadingModal}
      position="right"
      heading={formatText('user-management.admin.admin-details')}
      confirmButtonProps={{
        label: formatText('common.actions.save-changes'),
        variant: 'primary',
        onClick: handleSaveChanges,
        disabled: isSaving,
        loading: isSaving,
      }}
      buttonClassName="justify-end! "
    >
      {loadingModal ? (
        <AdminDetailsSkeleton />
      ) : (
        <div className="space-y-10 w-full bg-white">
          {/* Profile */}
          <div className="flex flex-col items-center gap-4">
            <AvatarGroupList
              list={[
                {
                  id: adminData?.id || '',
                  name:
                    adminData?.name ??
                    formatText('children.details.child-avatar'),
                  image: getAssetUrl(adminData?.asset),
                },
              ]}
              size={100}
              avatarClassName="w-25! h-25! text-4xl!"
              className="rounded-full overflow-hidden"
            />

            <div className="text-2xl font-bold leading-8 text-neutral-primary text-center tracking-[-0.24px]">
              {adminData?.name || '-'}
            </div>

            <div className="flex gap-4">
              <span className="px-2 py-1 rounded-full bg-success-light-50 text-success-secondary text-xs font-semibold">
                {adminData?.role?.name || '-'}
              </span>
            </div>
          </div>

          {/* Status */}
          <div className="flex justify-between">
            <div className="flex items-center gap-1.5">
              <Switch
                id="status"
                checked={adminData?.status === ECommonStatus.ACTIVE}
                onChange={handleStatusChange}
              />
              <span className="text-sm text-neutral-primary leading-5">
                {formatText('user-management.status')}:{' '}
                <span className="font-medium">
                  {adminData?.status === ECommonStatus.ACTIVE
                    ? formatText('common.status.active')
                    : formatText('common.status.inactive')}
                </span>
              </span>
            </div>
          </div>

          {/* Email */}
          <div className="flex gap-6">
            <div className="flex-1">
              <div className="flex gap-2">
                <IconMail size={16} className="text-neutral-500" />
                <span className="text-neutral-secondary text-sm">
                  {formatText('common.field.email')}
                </span>
              </div>
              <span className="ml-6 text-base mt-2 text-neutral-primary break-all">
                {adminData?.email || '-'}
              </span>
            </div>
            <div className="flex-1">
              <div className="flex gap-2">
                <IconCalendarStar size={16} className="text-neutral-500" />
                <span className="text-neutral-secondary text-sm">
                  {formatText('common.field.ac-created')}
                </span>
              </div>
              <span className="ml-6 text-base mt-2 text-neutral-primary">
                {adminData?.created_at
                  ? dayjs(Number(adminData.created_at) * 1000).format(
                      'MMM DD, YYYY'
                    )
                  : '-'}
              </span>
            </div>
          </div>

          {/* Permission & Access */}
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-2">
              <p className="text-neutral-primary text-lg font-semibold">
                {formatText('user-management.admin.permission-access')}
              </p>
              <span className="rounded-full bg-neutral-light-100 px-2 py-0.5 font-semibold text-xs text-neutral-primary">
                {activePermissionsCount.toString().padStart(2, '0')}
              </span>
            </div>

            <div className="border border-neutral-light-200 rounded-2xl p-6 space-y-4">
              {sections.map((item) => (
                <div
                  key={item.id}
                  className="flex items-center justify-between"
                >
                  <span className="text-neutral-primary text-base font-normal">
                    {formatText(SectionNameConfig[item.section_code])}
                  </span>
                  <Switch
                    id={item.id}
                    checked={item.has_access}
                    onChange={() => handlePermissionChange(item.id)}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </CustomModal>
  );
};

export default AdminDetailModal;
